import { t } from '@trimble/i18n';
import { PathNavigationCapability } from '@trimble/web3d-capabilities';
import { createSignal, JSX, Show, createEffect, batch } from 'solid-js';
import { PathNavigationStatus } from '@trimble/web3d-plugin-path-navigation';
import { CustomInput } from '@tcweb/connect-ui';
import {
  fixModusSliderStyle,
  UvIconButton,
} from '@trimble/ui-components/solidjs';
import { convertLength, getChainageUnit } from '@trimble/utilities';
import { LengthUnit } from '@fulcrum/lib';

const DEFAULT_NAVIGATION_SPEED = 30;
const DEFAULT_LENGTH_UNIT: LengthUnit = 'm';
const DEFAULT_LENGTH_DECIMALS = 2;

export const AlignmentNavigationDialog = (props: {
  pathNavigationCapability: PathNavigationCapability | undefined;
  isOpen: boolean;
  lengthUnit?: LengthUnit;
  lengthDecimals?: number;
}): JSX.Element => {
  const [navigationSpeed, setNavigationSpeed] = createSignal(
    DEFAULT_NAVIGATION_SPEED
  );

  // Local signal for chainage input to prevent feedback loop during typing
  const [chainageInputValue, setChainageInputValue] = createSignal('');
  const [isChainageInputFocused, setIsChainageInputFocused] =
    createSignal(false);

  const getLengthUnit = (): LengthUnit =>
    getChainageUnit(props.lengthUnit ?? DEFAULT_LENGTH_UNIT);
  const getLengthDecimals = (): number =>
    props.lengthDecimals ?? DEFAULT_LENGTH_DECIMALS;

  // Initialize slider styles
  fixModusSliderStyle();

  // Sync navigation speed with capability
  createEffect(() => {
    if (props.pathNavigationCapability && props.isOpen) {
      setNavigationSpeed(props.pathNavigationCapability.autoPilotSpeed);
      // GetCurrent position from the tool
      // const point = tool.currentAlignmentPoint;
      // now would need to call alignment API to get chainage at this point
    }
  });

  // Sync chainage input value with capability's chainage when not focused
  // Capability's chainage is always in meters, convert to display unit
  createEffect(() => {
    const chainageMeters = props.pathNavigationCapability?.chainage();
    if (chainageMeters !== undefined && !isChainageInputFocused()) {
      const displayValue =
        convertLength(chainageMeters, 'm', getLengthUnit()) ?? chainageMeters;
      setChainageInputValue(displayValue.toFixed(getLengthDecimals()));
    }
  });

  // Handle navigation speed change
  const handleSpeedChange = (value: number): void => {
    setNavigationSpeed(value);
    if (props.pathNavigationCapability) {
      props.pathNavigationCapability.autoPilotSpeed = value;
    }
  };

  const handleChainageJump = (value: string): void => {
    const chainageInDisplayUnit = parseFloat(value);
    if (
      isNaN(chainageInDisplayUnit) ||
      !props.pathNavigationCapability ||
      props.pathNavigationCapability.status() !==
        PathNavigationStatus.Navigating
    ) {
      return;
    }

    // Convert from display unit to meters (capability uses meters internally)
    const chainageMeters =
      convertLength(chainageInDisplayUnit, getLengthUnit(), 'm') ??
      chainageInDisplayUnit;

    // Begin/end station from capability are already in meters
    const beginStation = props.pathNavigationCapability?.beginStation() ?? 0;
    const endStation = props.pathNavigationCapability?.endStation() ?? 1;

    let targetChainage: number;
    if (chainageMeters >= beginStation && chainageMeters <= endStation) {
      targetChainage = chainageMeters;
    } else if (chainageMeters < beginStation) {
      targetChainage = beginStation;
    } else {
      targetChainage = endStation;
    }

    void props.pathNavigationCapability?.navigateToPosition(targetChainage);
  };

  const handleChainageInputBlur = (): void => {
    batch(() => {
      setIsChainageInputFocused(false);
      // Format the value on blur to show proper decimals
      // Capability's chainage is in meters, convert to display unit
      const chainageMeters = props.pathNavigationCapability?.chainage();
      if (chainageMeters !== undefined) {
        const displayValue =
          convertLength(chainageMeters, 'm', getLengthUnit()) ?? chainageMeters;
        setChainageInputValue(displayValue.toFixed(getLengthDecimals()));
      }
    });
  };

  const handleChainageInputKeyDown = (e: KeyboardEvent): void => {
    if (e.key === 'Enter') {
      // Blur the DOM input to submit the value. This triggers onBlur which calls
      // handleChainageJump and handleChainageInputBlur, ensuring the DOM focus state
      // matches the isChainageInputFocused signal and preventing a race condition
      // where continued typing after Enter gets overwritten by the chainage sync effect.
      (e.target as HTMLInputElement)?.blur();
    }
  };

  const handleDistanceSlide = (distance: number): void => {
    if (
      props.pathNavigationCapability &&
      props.pathNavigationCapability.status() ===
        PathNavigationStatus.Navigating
    ) {
      void props.pathNavigationCapability?.navigateToPosition({
        distance,
      });
    }
  };

  return (
    <Show when={props.isOpen}>
      <div
        class="absolute top-full right-0 mt-1 bg-base-page border py-1 px-2 border-[var(--modecolor-modecontent-level2,#e0e1e9)] rounded-sm shadow-[0px_4px_6px_-1px_rgba(0,0,0,0.1),0px_2px_4px_-2px_rgba(0,0,0,0.1)] min-w-200"
        data-testid="alignment-navigation-dialog"
      >
        {/* Action Group - Toolbar buttons */}
        <div
          class={`flex items-center px-1 py-1 gap-3 rounded-sm bg-base-page min-h-[32px] min-w-[156px]`}
        >
          <UvIconButton
            dataTestId="align-snap-button"
            type="action"
            variant="outlined"
            tooltip={t('TC.3D.AlignmentNavigationToolbar.SnapButtonTooltip')}
            onClick={() =>
              void props.pathNavigationCapability?.snapToAlignment()
            }
            disabled={
              props.pathNavigationCapability?.status() !==
              PathNavigationStatus.Navigating
            }
            icon="tc-icon-snap-line"
          />
          <UvIconButton
            dataTestId="auto-pilot-button"
            type="toggle"
            variant="outlined"
            tooltip={t('web3D.toolbar.actions.autoPilot')}
            onClick={() =>
              void props.pathNavigationCapability?.toggleAutoPilot()
            }
            pressed={props.pathNavigationCapability?.autoPilot() || false}
            disabled={
              props.pathNavigationCapability?.status() !==
              PathNavigationStatus.Navigating
            }
            icon="play_circle"
          />
          <UvIconButton
            dataTestId="cursor-button"
            type="toggle"
            variant="outlined"
            tooltip={t('web3D.toolbar.actions.pickPath')}
            onClick={() =>
              void props.pathNavigationCapability?.togglePickingActive()
            }
            pressed={props.pathNavigationCapability?.pickingActive() || false}
            disabled={
              props.pathNavigationCapability?.status() ===
              PathNavigationStatus.Inactive
            }
            icon="cursor"
            iconVariant={
              props.pathNavigationCapability?.pickingActive()
                ? 'solid'
                : 'outlined'
            }
          />
        </div>

        <Show
          when={
            props.pathNavigationCapability?.autoPilot() &&
            props.pathNavigationCapability?.status() ===
              PathNavigationStatus.Navigating
          }
        >
          <div class="flex flex-col gap-2 mt-2">
            <div class="text-xs">
              {t('TC.3D.AlignmentNavigationDialog.NavigationSpeed')}
            </div>
            <div class="flex flex-col gap-2">
              <modus-wc-slider
                attr:value={navigationSpeed()}
                attr:min={1}
                attr:max={800}
                attr:step={1}
                on:inputChange={(e) => {
                  const speed = parseFloat(
                    (e.detail.target as HTMLInputElement).value
                  );
                  handleSpeedChange(speed);
                }}
              />
            </div>
          </div>
        </Show>

        <Show
          when={
            props.pathNavigationCapability?.status() ===
            PathNavigationStatus.Navigating
          }
        >
          <div class="flex flex-col gap-2 mt-2">
            <div class="text-xs">
              {t('TC.3D.AlignmentNavigationDialog.PositionOnPath')}
            </div>
            <modus-wc-slider
              attr:value={
                props.pathNavigationCapability?.distance() ??
                Math.round(props.pathNavigationCapability?.beginDistance() || 0)
              }
              attr:min={props.pathNavigationCapability?.beginDistance() || 0}
              attr:max={props.pathNavigationCapability?.endDistance() || 1}
              on:inputChange={(e) => {
                const distance = (e.detail.target as HTMLInputElement).value;
                handleDistanceSlide(parseFloat(distance));
              }}
            />

            <Show when={props.pathNavigationCapability?.isAlignment() === true}>
              <CustomInput
                name="chainage-input"
                label={t('TC.3D.AlignmentNavigationDialog.JumpToChainage')}
                type="number"
                placeholder=""
                value={chainageInputValue()}
                onInputRef={(ref) => ref && (ref.min = '0')}
                onInput={(
                  e: InputEvent & {
                    currentTarget: HTMLInputElement;
                    target: Element;
                  }
                ) => {
                  const value = e.currentTarget.value;
                  if (Number(value) < 0) {
                    e.currentTarget.value = chainageInputValue();
                    return;
                  }
                  setChainageInputValue(value);
                  // Native stepper (spinner buttons / arrow keys) reports an
                  // empty inputType; commit immediately so the camera moves
                  // without needing Enter or blur. Typed input still waits.
                  if (!e.inputType) {
                    handleChainageJump(value);
                  }
                }}
                onBlur={() => {
                  handleChainageJump(chainageInputValue());
                  handleChainageInputBlur();
                }}
                onFocus={() => setIsChainageInputFocused(true)}
                onKeyDown={(e) => handleChainageInputKeyDown(e)}
                disabled={
                  props.pathNavigationCapability?.status() !==
                  PathNavigationStatus.Navigating
                }
                class="text-xs"
                data-cy="chainage-input"
              />
              <Show
                when={props.pathNavigationCapability?.isInOverLength() === true}
              >
                <span class="text-xs mt-1 text-[var(--modus-wc-color-secondary)]">
                  {t('TC.3D.AlignmentNavigationDialog.InOverLength')}
                </span>
              </Show>
            </Show>
          </div>
        </Show>
      </div>
    </Show>
  );
};
